import { SignalObject } from "./signal-object";

export class BaseSignalLine extends SignalObject {
  constructor(settings) {
    super(settings);

    this.settings.ppmm = this.settings.ppcm / 10; // pixels per mm

    const parentSettings = this.settings.parent.ref.settings;
    this.uniforms = {
      u_color: new Float32Array(this.settings.color),
      u_shift: new Float32Array([0.0, this.settings.relatedLocation.y]),
      u_rollIndex: 0,
      u_scale: new Float32Array([1, 1]),
      u_windowSize: new Float32Array([parentSettings.clientSize.width, parentSettings.clientSize.height])
    };
  }

  setSignalBufferSettings(key, value) {
    this.signalBuffer.settings[key] = value;
  }

  setIsolineChangeAction(action) {
    this.signalBuffer.onIsolineChangeAction = function (isoline) {
      action(isoline);
    }.bind(this);
  }

  tryUpdateMeanSignalValue() {}

  getAmpl(x) {
    return this.signalBuffer.getAmpl(Math.floor(x));
  }

  getMinMax(beginIndex, endIndex) {
    return this.signalBuffer.getMinMax(beginIndex, endIndex);
  }

  // points in user units (e.g. mV) or in pixels (if valueConverter is not set)
  addPoints(points) {
    let startGraphicIndex = 0;
    //this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.glBuffer);
    if (points?.length > 0) {
      this.signalBuffer.putData(points[0]);
      startGraphicIndex = this.signalBuffer.graphicIndex;
      for (let i = 1; i < points.length; i++) {
        this.signalBuffer.putData(points[i]);
      }
    }

    const endGraphicIndex = this.signalBuffer.graphicIndex + 1;
    const gl = this.settings.gl;

    gl.bindBuffer(gl.ARRAY_BUFFER, this.glBuffer);
    if (startGraphicIndex > endGraphicIndex) {
      let updatedBuffer = this.signalBuffer.pointBuffer.subarray(startGraphicIndex * 4, this.signalBuffer.graphicBufferLength * 4);
      gl.bufferSubData(gl.ARRAY_BUFFER, startGraphicIndex * 4 * 4, updatedBuffer); // index * valuesForLine * bytesForValue
      updatedBuffer = this.signalBuffer.pointBuffer.subarray(0, endGraphicIndex * 4);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, updatedBuffer); // index * valuesForLine * bytesForValue
    } else {
      let updatedBuffer = this.signalBuffer.pointBuffer.subarray(startGraphicIndex * 4, endGraphicIndex * 4);
      gl.bufferSubData(gl.ARRAY_BUFFER, startGraphicIndex * 4 * 4, updatedBuffer); // index * valuesForLine * bytesForValue
    }
  }

  getPointX(signalBufferIndex) {
    return this.signalBuffer.getPointX(signalBufferIndex);
  }

  getData(signalIndex) {
    return this.signalBuffer.getData(signalIndex);
  }

  getCurrPointX() {
    return this.signalBuffer.getCurrPointX();
  }

  getCurrPointIndex() {
    return this.signalBuffer.getCurrPointIndex();
  }

  getPointsCount() {
    return this.signalBuffer.graphicDataBufferLength;
  }

  getLastValidGraphicDataIndex() {
    return this.signalBuffer.getLastValidGraphicDataIndex();
  }

  getLastValidPointX() {
    return this.signalBuffer.getLastValidPointX();
  }

  getBufferData(x) {
    return this.signalBuffer.getGraphicDataPoint(x);
  }

  getFirstBufferIndex(x) {
    return this.signalBuffer.getFirstGraphicDataIndex(Math.round(x));
  }

  getLastBufferIndex(x) {
    return this.signalBuffer.getLastGraphicDataIndex(Math.round(x));
  }

  setFilters() {}

  setScaleX(newScaleX) {
    this.signalBuffer.setScaleX(newScaleX * this.settings.viewScale.x);
    this.settings.gl.bindBuffer(this.settings.gl.ARRAY_BUFFER, this.glBuffer);
    //this.settings.gl.bufferSubData(this.settings.gl.ARRAY_BUFFER, 0, this.signalBuffer.pointBuffer);
    this.settings.gl.bufferData(this.settings.gl.ARRAY_BUFFER, this.signalBuffer.pointBuffer, this.settings.gl.DYNAMIC_DRAW);
  }

  setScaleY(newScaleY) {
    this.signalBuffer.setScaleY(newScaleY * this.settings.viewScale.y);
    this.settings.gl.bindBuffer(this.settings.gl.ARRAY_BUFFER, this.glBuffer);
    this.settings.gl.bufferSubData(this.settings.gl.ARRAY_BUFFER, 0, this.signalBuffer.pointBuffer);
  }

  setFrequency(freq) {
    this.signalBuffer.setFreq(freq);
    this.settings.gl.bindBuffer(this.settings.gl.ARRAY_BUFFER, this.glBuffer);
    this.settings.gl.bufferSubData(this.settings.gl.ARRAY_BUFFER, 0, this.signalBuffer.pointBuffer);
  }

  isNextPointGraphicBegin() {
    return this.signalBuffer.isNextPointGraphicBegin();
  }

  createBorders() {
    super.createBorders();
    this.updateDataBuffer();
  }

  updateDataBuffer() {
    const parentSettings = this.settings.parent.ref.settings;
    this.uniforms.u_shift[1] = this.settings.relatedLocation.y;
    this.uniforms.u_windowSize[0] = parentSettings.clientSize.width;
    this.uniforms.u_windowSize[1] = parentSettings.clientSize.height;

    this.signalBuffer.updateWindowSize(parentSettings.clientSize, parentSettings.clientSize);

    this.settings.gl.bindBuffer(this.settings.gl.ARRAY_BUFFER, this.glBuffer);
    this.settings.gl.bufferData(this.settings.gl.ARRAY_BUFFER, this.signalBuffer.pointBuffer, this.settings.gl.DYNAMIC_DRAW);
  }

  resetBuffer() {
    this.signalBuffer.reset();
    this.settings.gl.bindBuffer(this.settings.gl.ARRAY_BUFFER, this.glBuffer);
    this.settings.gl.bufferData(this.settings.gl.ARRAY_BUFFER, this.signalBuffer.pointBuffer, this.settings.gl.DYNAMIC_DRAW);
  }

  draw(gl, program) {
    if (this.signalBuffer.graphicIndex < 0) {
      return;
    }
    const parentSettings = this.settings.parent.ref.settings;
    gl.viewport(
      parentSettings.absoluteLocation.x,
      parentSettings.absoluteLocation.y,
      parentSettings.clientSize.width,
      parentSettings.clientSize.height
    );
    program.setUnforms(this.uniforms);

    gl.enableVertexAttribArray(program.attribs.a_position);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.glBuffer);
    gl.vertexAttribPointer(program.attribs.a_position, 2, gl.FLOAT, false, 0, 0);

    if (this.signalBuffer.isColumnMode) {
      gl.drawArrays(gl.LINES, 0, this.signalBuffer.actualGraphicBufferLength * 2);
    } else {
      gl.drawArrays(gl.LINE_STRIP, 0, this.signalBuffer.actualGraphicBufferLength * 2);
    }
  }
}
